/**
 * Translation Context
 * Provides translation functionality throughout the application
 * Reads selected language from localStorage and provides translated labels
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTranslations, getTranslation, defaultLanguage } from '../translations';

// Create the context
const TranslationContext = createContext(null);

/**
 * Translation Provider Component
 * Wraps the application to provide translation context
 */
export const TranslationProvider = ({ children }) => {
  // Get initial language from localStorage or use default
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      if (saved) {
        const lang = JSON.parse(saved);
        return lang.code || defaultLanguage;
      }
    } catch (e) {
      // Ignore parsing errors
    }
    return defaultLanguage;
  });

  // Get the translations for current language
  const [translations, setTranslations] = useState(() => getTranslations(currentLanguage));

  // Listen for language changes (from LanguageSelector component)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedLanguage' && e.newValue) {
        try {
          const lang = JSON.parse(e.newValue);
          if (lang.code && lang.code !== currentLanguage) {
            setCurrentLanguage(lang.code);
            setTranslations(getTranslations(lang.code));
          }
        } catch (err) {
          // Ignore parsing errors
        }
      }
    };

    // Also check localStorage periodically for same-tab changes
    const checkLanguage = () => {
      try {
        const saved = localStorage.getItem('selectedLanguage');
        if (saved) {
          const lang = JSON.parse(saved);
          if (lang.code && lang.code !== currentLanguage) {
            setCurrentLanguage(lang.code);
            setTranslations(getTranslations(lang.code));
          }
        }
      } catch (e) {
        // Ignore
      }
    };

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', handleStorageChange);

    // Check on interval for same-tab changes (since LanguageSelector uses localStorage)
    const intervalId = setInterval(checkLanguage, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [currentLanguage]);

  /**
   * Get a translated string by path
   * @param {string} path - Dot-separated path (e.g., 'header.signIn')
   * @param {Object} params - Parameters for interpolation
   * @returns {string} Translated string
   */
  const t = useCallback((path, params = {}) => {
    return getTranslation(currentLanguage, path, params);
  }, [currentLanguage]);

  /**
   * Change the current language
   * @param {string} languageCode - New language code
   */
  const changeLanguage = useCallback((languageCode) => {
    const code = languageCode?.toUpperCase() || defaultLanguage;
    setCurrentLanguage(code);
    setTranslations(getTranslations(code));
  }, []);

  // Context value
  const value = {
    currentLanguage,
    translations,
    t,
    changeLanguage
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Hook to access translation context
 * @returns {Object} Translation context value
 */
export const useTranslation = () => {
  const context = useContext(TranslationContext);

  if (!context) {
    // Return a fallback if used outside provider (for backward compatibility)
    return {
      currentLanguage: defaultLanguage,
      translations: getTranslations(defaultLanguage),
      t: (path, params = {}) => getTranslation(defaultLanguage, path, params),
      changeLanguage: () => {}
    };
  }

  return context;
};

export default TranslationContext;
